/**
 * Payment Success Modal
 *
 * Shown after successful payment redirect to confirm subscription details.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Zap, Calendar, Crown, X, Sparkles } from 'lucide-react';

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  credits: number;
  validUntil?: Date;
  isLoading?: boolean;
}

export const PaymentSuccessModal: React.FC<PaymentSuccessModalProps> = ({
  isOpen,
  onClose,
  planName,
  credits,
  validUntil,
  isLoading: _isLoading = false,
}) => {
  // Determine display plan name
  const displayPlanName = planName.toLowerCase() === 'free' && credits > 0
    ? 'Credits Added'
    : planName;
  // Format the validity date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Calculate days remaining
  const getDaysRemaining = (date: Date) => {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // Default to end of current month if no date provided
  const endDate = validUntil || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);
  const daysRemaining = getDaysRemaining(endDate);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-sm bg-white rounded-2xl overflow-hidden shadow-xl"
        >
          {/* Success Header */}
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-6 text-center relative overflow-hidden">
            {/* Decorative sparkles */}
            <div className="absolute top-4 left-4 text-white/30">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="absolute bottom-4 right-4 text-white/30">
              <Sparkles className="w-8 h-8" />
            </div>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', damping: 15 }}
              className="w-16 h-16 bg-white rounded-full mx-auto flex items-center justify-center mb-4"
            >
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-xl font-bold text-white mb-1"
            >
              Payment Successful!
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white/80 text-sm"
            >
              {displayPlanName === 'Credits Added'
                ? 'Your credits have been added'
                : `Welcome to ContentSage ${displayPlanName}`}
            </motion.p>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            {/* Plan Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-4 p-4 bg-indigo-50 rounded-xl"
            >
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Your Plan</p>
                <p className="text-lg font-bold text-gray-900">{displayPlanName}</p>
              </div>
            </motion.div>

            {/* Credits Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl"
            >
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Credits Available</p>
                <p className="text-lg font-bold text-gray-900">{credits} Credits</p>
              </div>
            </motion.div>

            {/* Validity Info - only show for subscriptions */}
            {displayPlanName !== 'Credits Added' && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valid Until</p>
                  <p className="text-lg font-bold text-gray-900">{formatDate(endDate)}</p>
                  <p className="text-xs text-gray-400">{daysRemaining} days remaining</p>
                </div>
              </motion.div>
            )}

            {/* For credit packs, show that credits don't expire */}
            {displayPlanName === 'Credits Added' && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 }}
                className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Credits Never Expire</p>
                  <p className="text-lg font-bold text-gray-900">Use anytime</p>
                </div>
              </motion.div>
            )}

            {/* CTA Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              onClick={onClose}
              className="w-full py-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
            >
              Start Creating
            </motion.button>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentSuccessModal;
